<script>
import DataLayer from './../../../services/data-layer/index'
import analyticsService from './../../../services/analytics-service'
import { mapGetters } from 'vuex'
import { timer } from './../../../utils/time'
import { supportEmail } from './../../../configuration'
import paywallTracker from '@services/mixpanel/paywall-tracker'
import { ENTITLEMENT_RESULTS } from '@services/mixpanel/analytics-spec'
import premiumFunnelOrigin from '@utils/premium-funnel-origin'

const DELAYED_TIMEOUT_IN_S = 20
const FAILURE_TIMEOUT_IN_S = 60
const POLLING_DELAY_IN_MS = 2000
const COUNTER_TIMER_INTERVAL_IN_MS = 1000

export default {
    name: 'PremiumComplete',
    data() {
        return {
            isProcessComplete: false,
            counter: 0,
            counterTimer: null,
            isDelayed: false,
            status: 'WAITING', // WAITING, COMPLETED, ERROR,
            isPaymentSuccessful: false,
            supportEmail,
            entitlementResultTracked: false,
        }
    },
    computed: {
        ...mapGetters('premium', ['activeSubscription', 'paymentMethod']),
        countingDots() {
            if (this.counter % 3 === 0) return '.'
            if (this.counter % 3 === 1) return '..'
            return '...'
        },
    },
    watch: {
        paymentMethod() {
            if (this.paymentMethod && this.activeSubscription) this.track()
        },
    },
    mounted() {
        // The funnel ends here; a remembered origin must not leak into a
        // later, unrelated funnel exit.
        premiumFunnelOrigin.clear()
        this.isPaymentSuccessful = this.$route.query.state === 'success'
        if (this.isPaymentSuccessful) {
            const productId = this.$route.query.product_id || null
            const transactionId = this.$route.query.transaction_id || null

            // Plan breakdown is restored from localStorage inside the tracker
            // (captured at checkout, before the payment redirect).
            paywallTracker.onPurchaseSucceeded()

            // Track entitlement sync start - now waiting for subscription activation
            paywallTracker.onEntitlementSyncStarted(productId, transactionId)
            this.startSubscriptionPolling()
        }
        this.startCounting()
    },
    beforeDestroy() {
        if (this.counterTimer) {
            clearInterval(this.counterTimer)
            this.counterTimer = null
        }
    },
    methods: {
        timer,
        track() {
            analyticsService.premiumUpsellCompleted({
                isSuccessful: this.isPaymentSuccessful,
                planId: this.activeSubscription?.plan?.id,
                amount: this.activeSubscription?.plan?.price,
                method: this.paymentMethod,
            })
        },
        async startSubscriptionPolling() {
            if(this.status === 'COMPLETED') return

            await this.getSubscriptions()
            await this.timer(POLLING_DELAY_IN_MS)
            this.startSubscriptionPolling()
        },
        async getSubscriptions() {
            const premiumService = DataLayer.subscriptions
            const memberId = this.$store.state.auth.currentUser?.members_id?.id
            const subscriptions = await premiumService.getSubscriptionsByMemberId(memberId)
            if(subscriptions.body?.some(subscription => subscription.status === 'active')) {
                this.status = 'COMPLETED'
                this._trackEntitlementResult(ENTITLEMENT_RESULTS.SUCCESS)
            }
        },
        startCounting() {
            this.counter = 0
            this.counterTimer = setInterval(() => {
                if(this.status === 'COMPLETED') {
                    clearInterval(this.counterTimer)
                    this.counterTimer = null
                    return
                }
                this.counter++

                if (this.counter >= DELAYED_TIMEOUT_IN_S) {
                    this.isDelayed = true
                }
                if (this.counter >= FAILURE_TIMEOUT_IN_S) {
                    this.isDelayed = false
                    this.status = 'ERROR'
                    this._trackEntitlementResult(ENTITLEMENT_RESULTS.TIMEOUT)
                    clearInterval(this.counterTimer)
                    this.counterTimer = null
                }
            }, COUNTER_TIMER_INTERVAL_IN_MS)
        },
        _trackEntitlementResult(result) {
            if (this.entitlementResultTracked) return
            this.entitlementResultTracked = true
            const transactionId = this.$route.query.transaction_id || null
            paywallTracker.onEntitlementSyncResult(result, transactionId)
        },
        onBeginListening() {
            window.location.href = '/'
        },
    },
}
</script>

<template>
    <div :class="$style.welcome">
        <BaseSection padded>
            <div v-if="status === 'WAITING'">
                <h1>Activating Premium {{ countingDots }}</h1>
                <h6 :class="$style.description">
                    {{
                        isDelayed
                            ? 'This is taking a bit longer than expected. Please be patient...'
                            : 'Please wait a few seconds while we upgrade your account...'
                    }}
                </h6>
            </div>
            <div v-else-if="status === 'COMPLETED'">
                <h1 :class="$style.mainHeader">All Set!</h1>
                <h6 :class="$style.description">Thanks for upgrading to Premium!</h6>
                <div :class="$style.buttons">
                    <BaseButton
                        button-type="highlight"
                        @click="onBeginListening"
                    >BEGIN LISTENING</BaseButton>
                </div>
            </div>
            <div v-else-if="status === 'ERROR'">
                <h1 :class="$style.mainHeader">Activating Premium</h1>
                <h6 :class="$style.description">Looks like something went wrong. Please contact <u><a :href="`mailto:${supportEmail}`">support</a></u>.</h6>
            </div>
        </BaseSection>
    </div>
</template>

<style lang="scss" module>
@use '@design' as *;

.mainHeader {
    @include h3;

    letter-spacing: -0.5px;
}

.description {
    margin-bottom: 50px;
    font-weight: 300;
    line-height: 1.5;

    @extend %heading;
}

@include desktop {
    .mainHeader {
        @include h1;

        margin-bottom: $s-xxl;
    }

    .welcomeButton ~ .welcomeButton {
        margin-left: $s-l;
    }

    .description {
        margin-bottom: 100px;
    }
}


</style>
